// "Travel" between form fields with the arrow keys. Used by the inline editors,
// the custom Select and AsyncPicker so the arrows move focus to the neighbouring
// field instead of being trapped inside a number/date input (Up/Down spinning
// the value, Left/Right walking the caret/date segments).
//
// Two strategies, tried in order:
//  1. STRUCTURAL (preferred) — when the field lives in a <dl> label/value grid
//     (the document header), navigate by grid position: row N of one column sits
//     across from row N of the next. Exact, and matches what the user sees even
//     when columns have different field counts/heights. See structuralMove.
//  2. GEOMETRIC (fallback) — pure on-screen nearest-field, for everything else
//     (totals panel, transitions out of the header, etc.):
//       - Up/Down stay in the SAME COLUMN (smallest horizontal offset), then
//         nearest vertically.
//       - Left/Right stay in the SAME ROW (smallest vertical offset), then
//         nearest horizontally.
type VDir = 'up' | 'down';
type HDir = 'left' | 'right';

const FIELD_SELECTOR =
  'input:not([type="hidden"]):not([disabled]):not([readonly]), select:not([disabled]), textarea:not([disabled]):not([readonly]), [contenteditable="true"], [data-travel]';

// Travel stays within the user's current "surface": if focus is inside a modal
// dialog, only fields in that dialog are candidates; otherwise dialogs are
// excluded so page travel never jumps into (or behind) an open modal. The nav
// chrome (main/sub nav, sidebars) is always off-limits.
function inScope(from: HTMLElement, el: HTMLElement): boolean {
  if (el.closest('nav, aside, [role="navigation"]')) return false;
  const dlg = from.closest('[role="dialog"]');
  if (dlg) return dlg.contains(el);
  return !el.closest('[role="dialog"]');
}

function land(target: HTMLElement) {
  target.focus();
  if (target instanceof HTMLInputElement && (target.type === 'text' || target.type === 'number')) {
    try { target.select(); } catch { /* some input types disallow select() */ }
  }
}

// The first visible, on-screen travel target inside a value cell (<dd>).
function fieldIn(dd: Element | undefined | null): HTMLElement | null {
  if (!dd) return null;
  const el = dd.querySelector<HTMLElement>(FIELD_SELECTOR);
  return el && el.offsetParent !== null ? el : null;
}

// Structural travel through a label/value grid built from <dl> columns (the
// document header: each column is a <dl> of <dt>/<dd> rows, and the columns are
// sibling <dl>s under one container). This matches the user's mental model
// EXACTLY — "row N of column A sits across from row N of column B" — regardless
// of pixel alignment when columns have different field counts or heights:
//   - Up/Down step to the next focusable <dd> in the SAME column.
//   - Left/Right step to the same-row <dd> in the ADJACENT column (nearest
//     focusable row if that exact row isn't a travel stop).
// Returns false — so the caller falls back to geometry — when the field isn't
// in such a grid (line tables, the totals panel, single-field custom-field
// rows) or there's no neighbour in that direction (e.g. off the end of a
// column, which should drop into the lines table below via geometry).
function structuralMove(from: HTMLElement, dir: VDir | HDir): boolean {
  const dd = from.closest('dd');
  const dl = dd?.parentElement;
  if (!dd || !dl || dl.tagName !== 'DL') return false;
  const dds = Array.from(dl.children).filter((c) => c.tagName === 'DD') as HTMLElement[];
  if (dds.length < 2) return false; // single-row dl (e.g. a custom-field tab) → geometry
  const rowIdx = dds.indexOf(dd);
  if (rowIdx < 0) return false;

  if (dir === 'up' || dir === 'down') {
    const step = dir === 'down' ? 1 : -1;
    for (let i = rowIdx + step; i >= 0 && i < dds.length; i += step) {
      const f = fieldIn(dds[i]);
      if (f) { land(f); return true; }
    }
    return false;
  }
  // Left/Right → the adjacent sibling column, same row (clamped), preferring
  // the exact row then expanding outward to the nearest focusable one.
  const cols = Array.from(dl.parentElement?.children || []).filter((c) => c.tagName === 'DL') as HTMLElement[];
  const targetDl = cols[cols.indexOf(dl) + (dir === 'right' ? 1 : -1)];
  if (!targetDl) return false;
  const tdds = Array.from(targetDl.children).filter((c) => c.tagName === 'DD') as HTMLElement[];
  const start = Math.min(rowIdx, tdds.length - 1);
  for (let d = 0; d < tdds.length; d++) {
    const f = fieldIn(tdds[start + d]) || (start - d >= 0 ? fieldIn(tdds[start - d]) : null);
    if (f) { land(f); return true; }
  }
  return false;
}

export function moveFocusVertical(from: HTMLElement, dir: VDir): boolean {
  if (structuralMove(from, dir)) return true;
  const r = from.getBoundingClientRect();
  const cy = r.top + r.height / 2;
  const cx = r.left + r.width / 2;

  const cands: Array<{ el: HTMLElement; vdist: number; hdist: number }> = [];
  document.querySelectorAll<HTMLElement>(FIELD_SELECTOR).forEach((el) => {
    if (el === from || el.offsetParent === null) return; // hidden
    if (!inScope(from, el)) return;
    const er = el.getBoundingClientRect();
    const ecy = er.top + er.height / 2;
    const dy = ecy - cy;
    if (dir === 'up' && dy > -4) return;
    if (dir === 'down' && dy < 4) return;
    cands.push({ el, vdist: Math.abs(dy), hdist: Math.abs(er.left + er.width / 2 - cx) });
  });
  if (!cands.length) return false;

  // Same column first (smallest horizontal offset), then nearest vertically.
  const minH = Math.min(...cands.map((c) => c.hdist));
  const col = cands.filter((c) => c.hdist <= minH + 12).sort((a, b) => a.vdist - b.vdist);
  land(col[0].el);
  return true;
}

export function moveFocusHorizontal(from: HTMLElement, dir: HDir): boolean {
  if (structuralMove(from, dir)) return true;
  const r = from.getBoundingClientRect();
  const cy = r.top + r.height / 2;
  const cx = r.left + r.width / 2;

  const cands: Array<{ el: HTMLElement; hdist: number; vdist: number }> = [];
  document.querySelectorAll<HTMLElement>(FIELD_SELECTOR).forEach((el) => {
    if (el === from || el.offsetParent === null) return; // hidden
    if (!inScope(from, el)) return;
    const er = el.getBoundingClientRect();
    const ecx = er.left + er.width / 2;
    const dx = ecx - cx;
    if (dir === 'left' && dx > -4) return;
    if (dir === 'right' && dx < 4) return;
    cands.push({ el, hdist: Math.abs(dx), vdist: Math.abs(er.top + er.height / 2 - cy) });
  });
  if (!cands.length) return false;

  // Same row first (smallest vertical offset), then nearest horizontally.
  const minV = Math.min(...cands.map((c) => c.vdist));
  const row = cands.filter((c) => c.vdist <= minV + 12).sort((a, b) => a.hdist - b.hdist);
  land(row[0].el);
  return true;
}
